/* ===== Public Landing Page (promotes Zetyar Nexo) ===== */

import { useEffect, useState, type ComponentType } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Check, ChevronDown, LogIn, Menu, Moon, Sun, X } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { useTheme } from '@/presentation/providers/useTheme'
import { Button } from '@/presentation/components/ui/Button'
import { AUDIENCES, FAQS, MODULES, SECURITY_POINTS, SHOWCASE, STEPS, type ShowcaseKey } from './landingContent'
import {
  AnnouncementsIllustration,
  DuesIllustration,
  EventsIllustration,
  FinanceIllustration,
  HeroIllustration,
} from './landingIllustrations'
import { LeadForm } from './LeadForm'

const NAV_ITEMS = [
  { id: 'producto', label: 'Producto' },
  { id: 'modulos', label: 'Módulos' },
  { id: 'seguridad', label: 'Seguridad' },
  { id: 'preguntas', label: 'Preguntas' },
]

const SHOWCASE_ILLUSTRATIONS: Record<ShowcaseKey, ComponentType> = {
  dues: DuesIllustration,
  announcements: AnnouncementsIllustration,
  events: EventsIllustration,
  finance: FinanceIllustration,
}

const PAGE_TITLE = 'Zetyar Nexo | Gestión para organizaciones y comunidades'

// The app uses a hash router, so in-page links cannot be href="#section": that would change the
// route. Sections are reached with scrollIntoView instead.
function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export function LandingPage() {
  const navigate = useNavigate()
  const { user, profile } = useApp()
  const { theme, toggleTheme } = useTheme()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    const previousTitle = document.title
    document.title = PAGE_TITLE
    return () => {
      document.title = previousTitle
    }
  }, [])

  const goToSection = (id: string) => {
    setIsMenuOpen(false)
    scrollToSection(id)
  }

  const goToPanel = () => navigate(profile?.isSuperAdmin ? '/admin/saas' : '/dashboard')

  return (
    <div className="app-shell min-h-screen">
      <a
        href="#contenido"
        onClick={(event) => {
          event.preventDefault()
          document.getElementById('contenido')?.focus()
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-[10px] focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-700"
      >
        Saltar al contenido
      </a>

      <header className="sticky top-0 z-30 border-b border-neutral-200/70 bg-white/85 backdrop-blur-lg dark:border-neutral-800/70 dark:bg-neutral-950/80">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2.5 rounded-[10px]"
            aria-label="Zetyar Nexo, volver al inicio"
          >
            <BrandMark />
            <span className="whitespace-nowrap text-base font-extrabold tracking-tight text-neutral-900 dark:text-neutral-100">Zetyar Nexo</span>
          </button>

          <nav aria-label="Secciones" className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goToSection(item.id)}
                className="rounded-[10px] px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Usar tema claro' : 'Usar tema oscuro'}
              className="rounded-[10px] p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            {/* Wrapper carries the breakpoint: Button's own inline-flex would override a `hidden` class. */}
            <div className="hidden items-center gap-2 sm:flex">
              {user ? (
                <Button size="sm" variant="outline" rightIcon={<ArrowRight className="h-4 w-4" />} onClick={goToPanel}>
                  Ir a mi panel
                </Button>
              ) : (
                <Button size="sm" variant="outline" leftIcon={<LogIn className="h-4 w-4" />} onClick={() => navigate('/login')}>
                  Ingresar
                </Button>
              )}
              <Button size="sm" onClick={() => goToSection('contacto')}>
                Solicitar demo
              </Button>
            </div>
            <button
              type="button"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-expanded={isMenuOpen}
              aria-controls="landing-mobile-menu"
              aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              className="rounded-[10px] p-2 text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800 md:hidden"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <nav id="landing-mobile-menu" aria-label="Secciones" className="border-t border-neutral-200 px-4 pb-4 pt-2 dark:border-neutral-800 md:hidden">
            <div className="grid gap-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goToSection(item.id)}
                  className="rounded-[10px] px-3 py-2.5 text-left text-sm font-medium text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="mt-3 grid gap-2">
              {user ? (
                <Button variant="outline" fullWidth onClick={goToPanel}>
                  Ir a mi panel
                </Button>
              ) : (
                <Button variant="outline" fullWidth onClick={() => navigate('/login')}>
                  Ingresar
                </Button>
              )}
              <Button fullWidth onClick={() => goToSection('contacto')}>
                Solicitar demo
              </Button>
            </div>
          </nav>
        )}
      </header>

      <main id="contenido" tabIndex={-1} className="outline-none">
        <section className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-[radial-gradient(ellipse_at_top,rgba(91,92,240,0.16),transparent_60%)]"
          />
          <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:pb-24 lg:pt-20">
            <div>
              <p className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 dark:border-primary-500/30 dark:bg-primary-500/10 dark:text-primary-200">
                Plataforma para comunidades organizadas
              </p>
              <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-neutral-900 sm:text-5xl dark:text-neutral-50">
                Tu organización y sus miembros, en un solo lugar
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-neutral-600 dark:text-neutral-300">
                Comunicados, actividades, cuotas, finanzas y pagos en línea para asociaciones, fundaciones, clubes y comunidades.
                Cada organización con su propio espacio, aislado y seguro.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button size="lg" rightIcon={<ArrowRight className="h-5 w-5" />} onClick={() => goToSection('contacto')}>
                  Solicitar demo
                </Button>
                <Button size="lg" variant="outline" onClick={() => goToSection('producto')}>
                  Ver la plataforma
                </Button>
              </div>
              <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                {['Acceso solo por invitación', 'Pagos con Mercado Pago', 'Funciona en celular y computador'].map((item) => (
                  <li key={item} className="flex items-center gap-1.5">
                    <Check className="h-4 w-4 text-success-600 dark:text-success-200" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <Illustration caption="Vista ilustrativa del panel">
              <HeroIllustration />
            </Illustration>
          </div>
        </section>

        <section aria-labelledby="audiencias" className="border-y border-neutral-200 bg-white/60 dark:border-neutral-800 dark:bg-neutral-900/40">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <h2 id="audiencias" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400">
              Pensada para
            </h2>
            <ul className="flex flex-wrap gap-2">
              {AUDIENCES.map((audience) => (
                <li
                  key={audience}
                  className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
                >
                  {audience}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="producto" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-20 sm:px-6">
          <SectionHeading
            eyebrow="La plataforma"
            title="Así se ve el día a día de tu organización"
            description="Algunas vistas de los módulos que más usan las directivas y sus miembros."
            centered
          />
          <div className="mt-16 space-y-20 lg:space-y-28">
            {SHOWCASE.map((item, index) => {
              const ItemIllustration = SHOWCASE_ILLUSTRATIONS[item.key]
              const isReversed = index % 2 === 1
              return (
                <article key={item.key} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                  <div className={isReversed ? 'lg:order-2' : ''}>
                    <p className="text-sm font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">{item.eyebrow}</p>
                    <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl dark:text-neutral-50">{item.title}</h3>
                    <p className="mt-4 text-base leading-relaxed text-neutral-600 dark:text-neutral-400">{item.description}</p>
                    <ul className="mt-6 space-y-3">
                      {item.points.map((point) => (
                        <li key={point} className="flex items-start gap-2.5 text-sm text-neutral-700 dark:text-neutral-300">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300" aria-hidden="true" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={`relative ${isReversed ? 'lg:order-1' : ''}`}>
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute -inset-6 rounded-[40px] bg-[radial-gradient(circle_at_center,rgba(91,92,240,0.12),transparent_70%)]"
                    />
                    <div className="relative mx-auto max-w-md">
                      <Illustration caption={`Vista ilustrativa: ${item.eyebrow}`}>
                        <ItemIllustration />
                      </Illustration>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </section>

        <section id="modulos" className="scroll-mt-20 border-y border-neutral-200 bg-white/60 dark:border-neutral-800 dark:bg-neutral-900/40">
          <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
            <SectionHeading
              eyebrow="Módulos"
              title="Lo que tu directiva hoy lleva en planillas y grupos de mensajería"
              description="Activa los módulos que tu organización necesita. Cada persona ve y hace solo lo que corresponde a su rol."
            />
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {MODULES.map(({ title, description, icon: Icon }) => (
                <article key={title} className="rounded-[18px] border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
                  <div className="mb-4 grid h-10 w-10 place-items-center rounded-[12px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-100">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-neutral-900 text-white">
          <div className="mx-auto grid max-w-6xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-primary-300">Cómo funciona</p>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight">De la invitación a la participación</h2>
              <ol className="mt-10 space-y-7">
                {STEPS.map((step, index) => (
                  <li key={step.title} className="flex gap-4">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary-500 text-sm font-bold" aria-hidden="true">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-semibold">{step.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-neutral-300">{step.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="self-center rounded-[22px] border border-white/10 bg-white/5 p-6 sm:p-8">
              <h3 className="text-xl font-bold">Una cuenta, varias organizaciones</h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300">
                Quien participa en una asociación y en un club usa la misma cuenta y cambia de espacio en un clic. Cada
                organización ve solo su propia membresía, nunca las demás.
              </p>
              <ul className="mt-6 space-y-2" aria-hidden="true">
                {['Asociación', 'Club deportivo', 'Fundación'].map((label, index) => (
                  <li
                    key={label}
                    className={`flex items-center justify-between rounded-[13px] border px-4 py-3 text-sm ${
                      index === 0 ? 'border-primary-400/60 bg-primary-500/15' : 'border-white/10 bg-white/5 text-neutral-300'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="grid h-7 w-7 place-items-center rounded-[9px] bg-white/10 text-xs font-bold">{label.charAt(0)}</span>
                      {label}
                    </span>
                    {index === 0 && <span className="text-xs font-semibold text-primary-200">Espacio activo</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="seguridad" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-20 sm:px-6">
          <SectionHeading
            eyebrow="Seguridad y datos"
            title="Los datos de tus miembros, protegidos desde la base de datos"
            description="La seguridad no depende de ocultar botones: cada permiso se valida en el servidor."
          />
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {SECURITY_POINTS.map(({ title, description, icon: Icon }) => (
              <div key={title} className="flex gap-4">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-100">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="preguntas" className="scroll-mt-20 border-t border-neutral-200 dark:border-neutral-800">
          <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
            <SectionHeading eyebrow="Preguntas frecuentes" title="Lo que suelen preguntarnos" centered />
            <div className="mt-10 divide-y divide-neutral-200 rounded-[18px] border border-neutral-200 bg-white dark:divide-neutral-800 dark:border-neutral-800 dark:bg-neutral-900">
              {FAQS.map((faq) => (
                <details key={faq.question} className="group p-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-base font-semibold text-neutral-900 dark:text-neutral-100 [&::-webkit-details-marker]:hidden">
                    {faq.question}
                    <ChevronDown className="h-5 w-5 shrink-0 text-neutral-400 transition-transform group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="contacto" className="scroll-mt-20 bg-gradient-to-b from-transparent to-primary-50/70 dark:to-primary-500/5">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <SectionHeading
                eyebrow="Solicitar demo"
                title="Conversemos sobre tu organización"
                description="Cuéntanos cuántos miembros tienen y qué necesitan gestionar. Te mostramos la plataforma y te ayudamos a elegir los módulos."
              />
              <ul className="mt-8 space-y-3 text-sm text-neutral-700 dark:text-neutral-300">
                {[
                  'Demostración guiada para tu directiva',
                  'Recomendación de módulos según lo que gestionan',
                  'Configuración inicial de tu espacio a cargo del equipo Zetyar',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[22px] border border-neutral-200 bg-white p-6 shadow-card-elevated sm:p-8 dark:border-neutral-800 dark:bg-neutral-900">
              <LeadForm />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-200 dark:border-neutral-800">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-8 text-sm text-neutral-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 dark:text-neutral-400">
          <div className="flex items-center gap-2.5">
            <BrandMark small />
            <span>© {new Date().getFullYear()} Zetyar SpA · Zetyar Nexo</span>
          </div>
          <div className="flex gap-4">
            <button type="button" onClick={() => goToSection('contacto')} className="hover:text-neutral-900 dark:hover:text-neutral-100">
              Contacto
            </button>
            <button type="button" onClick={() => navigate('/login')} className="hover:text-neutral-900 dark:hover:text-neutral-100">
              Ingresar
            </button>
          </div>
        </div>
      </footer>
    </div>
  )
}

function BrandMark({ small = false }: { readonly small?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`grid place-items-center bg-gradient-to-br from-primary-500 to-[#8e67ff] font-extrabold text-white shadow-glow ${
        small ? 'h-6 w-6 rounded-[8px] text-xs' : 'h-9 w-9 rounded-[12px] text-base'
      }`}
    >
      Z
    </span>
  )
}

function Illustration({ caption, children }: { readonly caption: string; readonly children: React.ReactNode }) {
  return (
    <figure>
      {children}
      <figcaption className="mt-3 text-center text-xs text-neutral-500 dark:text-neutral-400">{caption}</figcaption>
    </figure>
  )
}

function SectionHeading({
  eyebrow,
  title,
  description,
  centered = false,
}: {
  readonly eyebrow: string
  readonly title: string
  readonly description?: string
  readonly centered?: boolean
}) {
  return (
    <div className={centered ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
      <p className="text-sm font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">{eyebrow}</p>
      <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-neutral-900 dark:text-neutral-50">{title}</h2>
      {description && <p className="mt-4 text-base leading-relaxed text-neutral-600 dark:text-neutral-400">{description}</p>}
    </div>
  )
}
